import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { CountdownOverlay } from '../components/CountdownOverlay';
import { DezoomDots } from '../components/DezoomDots';
import { GuessForm } from '../components/GuessForm';
import { LikeButton } from '../components/LikeButton';
import { ZoomImage } from '../components/ZoomImage';
import type { PhotoAttributionData } from '../components/PhotoAttribution';
import { useGameCountdown } from '../hooks/useGameCountdown';
import { useAuth } from '../hooks/useAuth';
import { t, getLocale } from '../hooks/useT';
import { preloadImages } from '../utils/imageCache';

interface PracticePuzzleResponse {
  puzzle: {
    id: string;
    puzzleNumber: number;
    likeCount?: number;
    attribution?: PhotoAttributionData | null;
  };
  zoomLevels: { level: number; imageUrl: string }[];
}

interface GuessResponse {
  correct: boolean;
  status: string;
  dezoomsUsed: number;
  currentZoomLevel: number;
  score: number | null;
  puzzleId: string;
  answer?: string;
  answerEn?: string;
}

export function PracticePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const puzzleIdParam = params.get('puzzle');

  const [puzzle, setPuzzle] = useState<PracticePuzzleResponse | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [dezooms, setDezooms] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [finished, setFinished] = useState(false);
  const [won, setWon] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);
  const [answerEn, setAnswerEn] = useState<string | null>(null);
  const [countdownEnabled, setCountdownEnabled] = useState(false);

  const startRun = useCallback(async () => {
    if (runId || !puzzle) return;
    const res = await api<{ runId: string }>('/practice/runs/start', {
      method: 'POST',
      body: JSON.stringify({ puzzleId: puzzle.puzzle.id }),
    });
    setRunId(res.runId);
  }, [runId, puzzle]);

  const { display, revealed, ready } = useGameCountdown({
    enabled: countdownEnabled && !finished,
    onGo: startRun,
  });

  const loadPuzzle = useCallback(async () => {
    const path = puzzleIdParam
      ? `/practice/puzzle/${puzzleIdParam}`
      : '/practice/next';
    const data = await api<PracticePuzzleResponse>(path);
    setPuzzle(data);
    setZoomLevel(0);
    setDezooms(0);
    setFinished(false);
    setWon(false);
    setScore(null);
    setAnswer(null);
    setAnswerEn(null);
    setRunId(null);
    setCountdownEnabled(true);
    preloadImages(data.zoomLevels.map((z) => z.imageUrl));
  }, [puzzleIdParam]);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.isPremium) {
      navigate('/premium', { replace: true });
      return;
    }
    loadPuzzle().catch((e) => setError(e.message));
  }, [authLoading, user, navigate, loadPuzzle]);

  const handleGuess = async (guess: string): Promise<boolean | void> => {
    if (!runId) return;
    setBusy(true);
    setError('');
    try {
      const res = await api<GuessResponse>(`/practice/runs/${runId}/guess`, {
        method: 'POST',
        body: JSON.stringify({ guess }),
      });
      setDezooms(res.dezoomsUsed);
      setZoomLevel(res.currentZoomLevel);
      if (res.status !== 'in_progress') {
        setFinished(true);
        setWon(res.status === 'won');
        setScore(res.score ?? null);
        if (res.answer) setAnswer(res.answer);
        if (res.answerEn) setAnswerEn(res.answerEn);
        setCountdownEnabled(false);
        return;
      }
      return res.correct;
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || 'Erreur');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const handleNext = () => {
    navigate('/practice', { replace: true });
    loadPuzzle().catch((e) => setError(e.message));
  };

  const displayAnswer = getLocale() === 'en' && answerEn ? answerEn : answer;

  if (!puzzle) {
    return <main className="page play"><p className="loading">{error || 'Chargement…'}</p></main>;
  }

  return (
    <main className="page play">
      <header className="play-header">
        <div className="play-header-left">
          <span className="puzzle-num practice-badge">Mode infini</span>
        </div>
        <DezoomDots used={dezooms} />
      </header>

      <div className={`zoom-stage${revealed ? ' is-revealed' : ''}`}>
        <ZoomImage
          levels={puzzle.zoomLevels}
          activeLevel={zoomLevel}
          attribution={puzzle.puzzle.attribution}
        />
        <CountdownOverlay value={display} />
      </div>

      <footer className="play-footer">
        {error && <p className="play-error" role="alert">{error}</p>}
        {finished ? (
          <div className="practice-done">
            <p className="practice-result">{won ? 'Bravo !' : 'Raté.'}</p>
            {!won && displayAnswer && (
              <p className="result-answer">
                {t('result.answer')} <strong>{displayAnswer}</strong>
              </p>
            )}
            {won && score != null && score > 0 && (
              <p className="practice-score">{score} pts <span className="muted">(hors classement)</span></p>
            )}
            <LikeButton
              puzzleId={puzzle.puzzle.id}
              initialCount={puzzle.puzzle.likeCount}
            />
            <div className="practice-actions">
              <button type="button" className="btn primary" onClick={handleNext}>Encore une</button>
              <Link to="/gallery" className="btn secondary">Galerie</Link>
            </div>
          </div>
        ) : (
          <GuessForm onSubmit={handleGuess} disabled={busy || !ready || !runId} />
        )}
      </footer>
    </main>
  );
}
